import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ReactNode } from 'react';
import Button from '../form/Button';
import classNames from '../../utils/classNames';

type EmptyStateVariant = 'default' | 'featured' | 'brand';

interface EmptyStateProps {
    iconFontAwesome?: IconProp;
    header?: string;
    description?: ReactNode;
    actionButtonText?: string;
    actionButtonIconFontAwesome?: IconProp;
    actionButtonMethod?: () => void;
    variant?: EmptyStateVariant;
    showActionButton?: boolean;
}

function renderIcon(iconFontAwesome: IconProp | undefined, variant: EmptyStateVariant): ReactNode {
    if (iconFontAwesome) {
        return (
            <FontAwesomeIcon icon={iconFontAwesome} size={variant === 'featured' ? '2xl' : 'xl'} />
        );
    }

    return null;
}

function renderActionIcon(actionButtonIconFontAwesome: IconProp | undefined): ReactNode {
    if (actionButtonIconFontAwesome) {
        return <FontAwesomeIcon icon={actionButtonIconFontAwesome} className="mr-2" />;
    }

    return null;
}

export function EmptyState({
    iconFontAwesome,
    header,
    description,
    actionButtonText,
    actionButtonIconFontAwesome,
    actionButtonMethod,
    variant = 'default',
    showActionButton = true,
}: EmptyStateProps): JSX.Element {
    const icon = renderIcon(iconFontAwesome, variant);
    const actionIcon = renderActionIcon(actionButtonIconFontAwesome);
    const shouldShowActionButton = showActionButton && !!actionButtonText;
    const actionButtonVariant = variant === 'default' ? 'secondary' : 'primary';

    return (
        <div className="mt-20 flex flex-col items-center px-4 text-center">
            {icon && (
                <div
                    className={classNames(
                        'mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 text-gray-400',
                        {
                            'h-20 w-20': variant === 'featured',
                            'bg-[rgba(248,218,40,0.2)] text-[#f8da28]':
                                variant === 'featured' || variant === 'brand',
                        }
                    )}
                >
                    {icon}
                </div>
            )}
            {header && (
                <h3
                    className={classNames('text-lg font-medium leading-7 text-gray-900', {
                        'text-xl font-semibold': variant === 'featured',
                    })}
                >
                    {header}
                </h3>
            )}
            {description && (
                <p
                    className={classNames('mb-6 mt-1 text-sm leading-5 text-gray-500', {
                        'mb-8 mt-2 max-w-80': variant === 'featured',
                    })}
                >
                    {description}
                </p>
            )}
            {shouldShowActionButton && (
                <Button
                    type="button"
                    variant={actionButtonVariant}
                    onClick={actionButtonMethod}
                    className={classNames({
                        'px-6': variant === 'featured' || variant === 'brand',
                    })}
                >
                    {actionIcon}
                    {actionButtonText}
                </Button>
            )}
        </div>
    );
}
